import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Page from '../components/Page';
import blogPosts, { RotationMockupGraphic, WebsiteFirstGraphic } from '../data/blogPosts';

const GRAPHIC_COMPONENTS = {
  RotationMockupGraphic,
  WebsiteFirstGraphic,
};

const LISTING_SEO = {
  title: 'Scheduly Blog — Practical Guides for Church Scheduling & Volunteer Management',
  description:
    'Tips, tools, and real-world strategies to help your church coordinate volunteers, plan services, and save time. New posts weekly.',
  canonical: 'https://scheduly.org/blog',
  ogTitle: 'Scheduly Blog — Practical Guides for Church Leaders',
  ogDescription:
    'Tips and strategies to help your church coordinate volunteers, plan services, and save time.',
  twitterCard: 'summary',
  twitterTitle: 'Scheduly Blog — Practical Guides for Church Leaders',
  twitterDescription:
    'Tips and strategies to help your church coordinate volunteers, plan services, and save time.',
};

function PostCard({ post, featured = false }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block rounded-2xl border border-slate-200 overflow-hidden no-underline hover:shadow-md hover:border-indigo-200 transition-all"
    >
      {/* Gradient header */}
      <div
        className="flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${post.gradient.from}, ${post.gradient.to})`,
          minHeight: featured ? 160 : 120,
          padding: '1.25rem',
        }}
      >
        {(() => {
          const Graphic = post.heroGraphic && GRAPHIC_COMPONENTS[post.heroGraphic];
          return Graphic ? (
            <div className="w-full max-w-xs"><Graphic /></div>
          ) : (
            <span className="text-4xl">{post.icon}</span>
          );
        })()}
      </div>

      <div className="p-5">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        <h3
          className={`font-black text-slate-900 mb-2 group-hover:text-indigo-700 transition-colors leading-snug ${
            featured ? 'text-xl' : 'text-base'
          }`}
        >
          {post.title}
        </h3>

        <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>

        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>
            {post.author} · {post.date}
          </span>
          <span className="text-indigo-600 font-semibold group-hover:underline">
            Read {featured ? 'Article' : '→'}
          </span>
        </div>
        <div className="text-xs text-slate-400 mt-1">{post.readTime}</div>
      </div>
    </Link>
  );
}

function FeaturedPost({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block rounded-2xl border border-slate-200 overflow-hidden no-underline hover:shadow-lg hover:border-indigo-200 transition-all"
    >
      <div className="flex flex-col md:flex-row">
        {/* Left: graphic */}
        <div
          className="md:w-2/5 flex items-center justify-center p-8"
          style={{
            background: `linear-gradient(135deg, ${post.gradient.from}, ${post.gradient.to})`,
            minHeight: 260,
          }}
        >
          <div className="w-full max-w-xs">
            {(() => {
              const Graphic = post.heroGraphic && GRAPHIC_COMPONENTS[post.heroGraphic];
              return Graphic ? <Graphic /> : <RotationMockupGraphic />;
            })()}
          </div>
        </div>

        {/* Right: content */}
        <div className="md:w-3/5 p-8 flex flex-col justify-center">
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <h2 className="text-2xl font-black text-slate-900 mb-3 leading-snug group-hover:text-indigo-700 transition-colors">
            {post.title}
          </h2>

          <p className="text-slate-500 text-sm leading-relaxed mb-6">{post.excerpt}</p>

          <div className="flex items-center gap-4 text-xs text-slate-400 mb-5">
            <span>{post.author}</span>
            <span>·</span>
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>

          <span className="inline-flex items-center gap-1 text-sm font-bold text-indigo-600 group-hover:underline">
            Read Article →
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    ReactGA.send('pageview');
  }, []);

  const [featured, ...rest] = blogPosts;

  return (
    <Page seo={LISTING_SEO}>
      {/* Header */}
      <section
        className="text-center px-6 pt-16 pb-14"
        style={{
          background: 'linear-gradient(180deg, #eef2ff 0%, #f8f7ff 60%, #f8fafc 100%)',
        }}
      >
        <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
          Scheduly Blog
        </span>
        <h1 className="text-4xl font-black text-slate-900 mb-4 leading-tight">
          Practical Guides for Church Leaders
        </h1>
        <p className="text-slate-500 text-lg max-w-xl mx-auto">
          Tips, tools, and real-world strategies to help your church run smoothly.
        </p>
      </section>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Featured post */}
        {featured && (
          <div className="mb-12">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
              Featured Post
            </p>
            <FeaturedPost post={featured} />
          </div>
        )}

        {/* Grid — only renders once there are more posts */}
        {rest.length > 0 && (
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">
              More Articles
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post) => (
                <PostCard key={post.slug} post={post} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}
